// AIReviewToolCard — WP-9 contextual tool card for AI enrichment / review.
//
// Spec: `docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md`
//   §6.5 Card 9, §7.9, §8.9. Activates when
//   `SceneSelection.toolCardKind === 'ai'` (or any index node kind starting
//   with `ai`/`evidence`/`candidate`).
//
// Design rules enforced (from masterplan §8.9):
//   - AI output never claims trust without provenance — every row carries
//     confidence + source; rows missing either render as a `<Banner>`
//     warning instead.
//   - Approve / Reject = TONAL buttons (`variant="secondary"`, never
//     destructive).  Publish = primary CTA gated by a `<Dialog>` confirm.
//   - Confidence values mono with two-decimal precision.
//   - Tokens / approved primitives only; no raw hex / rgba / emoji.
//   - Material Symbols rounded; one icon per concept.
//   - Single primary CTA per action row.
//   - 40 px touch min.

import { useMemo, useState } from "react";
import {
  Banner,
  Button,
  Card,
  Chip,
  Dialog,
  Icon,
  Stack,
  Text,
  tokens,
  type ChipVariant,
} from "@/design";
import type { CandidateFact } from "@/app/scenePackage";
import type { SceneSelection, ToolCardKind } from "@/app/sceneSelection";
import { AiCandidateList, deriveListCounts } from "./AiCandidateList";
import {
  applyCandidateFilters,
  pivotToReadiness,
  runApprove,
  runPublish,
  runReject,
  type AiCandidateFilterChip,
} from "./aiCandidateAdapter";
import {
  createMockAiReviewClient,
  type AiReviewClient,
} from "./aiClient";

export type AiPermissionState = "allowed" | "read-only" | "denied";

export interface AIReviewToolCardProps {
  /** Candidate-fact records (typically `node.candidateFacts ?? []`). */
  readonly candidates: readonly CandidateFact[];
  /** Optional layer label used in the card header (`AI candidates · {layer}`). */
  readonly layerLabel?: string;
  /** Optional active selection used as the source for the readiness pivot. */
  readonly selection?: Pick<
    SceneSelection,
    "indexNodeId" | "layerId" | "sceneItemId" | "featureId" | "source"
  >;
  /** Loading flag — overlays a `<Spinner>` row. */
  readonly isLoading?: boolean;
  /** Error message — overlays an error `<Banner>`. */
  readonly error?: string | null;
  /** Permission state controls whether actions are enabled. */
  readonly permission?: AiPermissionState;
  /** Optional Ai client. Defaults to a no-op mock so the card is safe to
   *  render in defence-mode and tests. */
  readonly client?: AiReviewClient;
  /** Optional retry handler for the error state. */
  readonly onRetry?: () => void;
  /** Optional pivot override.  When omitted the card dispatches the
   *  WP-2 `viewer:tool-card-request` event with `kind: 'readiness'`. */
  readonly onPivotReadiness?: () => void;
  /** Test hook — defaults to `'card.ai.review'`. */
  readonly cardId?: string;
}

const FILTER_CHIPS: ReadonlyArray<{
  readonly chip: AiCandidateFilterChip;
  readonly label: string;
  readonly icon: "verified" | "help" | "rate_review";
  readonly variant: ChipVariant;
}> = [
  { chip: "verified", label: "Verified", icon: "verified", variant: "success" },
  {
    chip: "low-confidence",
    label: "Low confidence",
    icon: "help",
    variant: "warning",
  },
  {
    chip: "needs-review",
    label: "Needs review",
    icon: "rate_review",
    variant: "tertiary",
  },
];

export const AI_REVIEW_TOOL_CARD_ID = "card.ai.review";

/** Predicate the future `ToolCardDock` uses to decide whether to mount
 *  this card. Exported so other callers can reuse the same rule. */
export function aiReviewCardAppearsWhen(
  toolCardKind: ToolCardKind | undefined,
): boolean {
  return toolCardKind === "ai";
}

export function AIReviewToolCard(props: AIReviewToolCardProps) {
  const {
    candidates,
    layerLabel,
    selection,
    isLoading,
    error,
    permission = "allowed",
    client: clientProp,
    onRetry,
    onPivotReadiness,
    cardId = AI_REVIEW_TOOL_CARD_ID,
  } = props;

  const client = useMemo<AiReviewClient>(
    () => clientProp ?? createMockAiReviewClient(),
    [clientProp],
  );

  const [query, setQuery] = useState("");
  const [activeChips, setActiveChips] = useState<Set<AiCandidateFilterChip>>(
    new Set(),
  );
  const [statusOverrides, setStatusOverrides] = useState<
    Record<string, CandidateFact["reviewStatus"]>
  >({});
  const [busyCandidateIds, setBusyCandidateIds] = useState<Set<string>>(
    new Set(),
  );
  const [actionError, setActionError] = useState<string | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);

  const filtered = useMemo(
    () => applyCandidateFilters(candidates, query, activeChips),
    [candidates, query, activeChips],
  );

  const counts = useMemo(() => deriveListCounts(candidates), [candidates]);
  const pendingForPublish = useMemo(
    () =>
      filtered.filter((candidate) => {
        const override = statusOverrides[candidate.id];
        const status = override ?? candidate.reviewStatus;
        return status === "approved";
      }),
    [filtered, statusOverrides],
  );

  const isReadOnly = permission === "read-only";
  const isDenied = permission === "denied";
  const actionsDisabled = isReadOnly || isDenied || isLoading || Boolean(error);

  const toggleChip = (chip: AiCandidateFilterChip) => {
    setActiveChips((current) => {
      const next = new Set(current);
      if (next.has(chip)) next.delete(chip);
      else next.add(chip);
      return next;
    });
  };

  const markBusy = (id: string, busy: boolean) => {
    setBusyCandidateIds((current) => {
      const next = new Set(current);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleApprove = async (candidateId: string) => {
    if (actionsDisabled) return;
    setActionError(null);
    markBusy(candidateId, true);
    const result = await runApprove(client, candidateId);
    markBusy(candidateId, false);
    if (result.ok) {
      setStatusOverrides((current) => ({
        ...current,
        [candidateId]: "approved",
      }));
    } else {
      setActionError(result.error ?? "Approve failed.");
    }
  };

  const handleReject = async (candidateId: string) => {
    if (actionsDisabled) return;
    setActionError(null);
    markBusy(candidateId, true);
    const result = await runReject(client, candidateId);
    markBusy(candidateId, false);
    if (result.ok) {
      setStatusOverrides((current) => ({
        ...current,
        [candidateId]: "rejected",
      }));
    } else {
      setActionError(result.error ?? "Reject failed.");
    }
  };

  const handleConfirmPublish = async () => {
    if (actionsDisabled) return;
    setActionError(null);
    setPublishBusy(true);
    const ids = pendingForPublish.map((candidate) => candidate.id);
    const result = await runPublish(client, ids);
    setPublishBusy(false);
    setPublishOpen(false);
    if (!result.ok) {
      setActionError(result.error ?? "Publish failed.");
      return;
    }
    setStatusOverrides((current) => {
      const next = { ...current };
      for (const id of ids) next[id] = "approved";
      return next;
    });
  };

  const handlePivotReadiness = () => {
    if (onPivotReadiness) onPivotReadiness();
    else pivotToReadiness(selection);
  };

  const headerLabel = layerLabel
    ? `AI candidates · ${layerLabel}`
    : "AI candidates";

  return (
    <Card
      data-testid={cardId}
      level={1}
      header={
        <Stack
          direction="row"
          align="center"
          justify="space-between"
          gap={2}
          style={{ flexWrap: "wrap" }}
        >
          <Stack direction="row" align="center" gap={2}>
            <Icon name="smart_toy" size={24} decorative />
            <Text variant="subtitle" as="h3" style={{ margin: 0 }}>
              {headerLabel}
            </Text>
          </Stack>
          <Stack direction="row" align="center" gap={2} style={{ flexWrap: "wrap" }}>
            <Chip
              icon="auto_awesome"
              label={`${counts.total} candidate${counts.total === 1 ? "" : "s"}`}
              variant="tertiary"
              data-testid="ai-counts-total"
            />
            <Chip
              icon="pending"
              label={`${counts.pending} pending`}
              variant="warning"
              data-testid="ai-counts-pending"
            />
          </Stack>
        </Stack>
      }
    >
      {isDenied ? (
        <Banner
          severity="warning"
          title="Permission denied"
          body="AI candidate review requires the ai.review role. Ask your administrator if you need access."
          errorCode="AI-REVIEW-PERMISSION-DENIED"
          data-testid="ai-permission-denied"
        />
      ) : null}

      {isReadOnly ? (
        <Banner
          severity="info"
          title="Read-only project"
          body="You can review AI candidates but cannot approve, reject, or publish until the project is unlocked."
          errorCode="AI-REVIEW-READ-ONLY"
          data-testid="ai-permission-readonly"
        />
      ) : null}

      <Stack direction="column" gap={3} style={{ marginTop: tokens.space[2] }}>
        <Stack direction="row" align="center" gap={2} style={{ flexWrap: "wrap" }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: tokens.space[2],
              minHeight: tokens.density.touchMin,
              flex: `1 1 ${tokens.layout.popoverSm}px`,
              minWidth: 0,
              paddingInline: tokens.space[2],
              border: tokens.border.thin,
              borderRadius: tokens.radius.input,
              background: tokens.color.surface[0],
              color: tokens.color.ink.primary,
            }}
          >
            <Icon name="search" size={20} decorative />
            <input
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder="Search predicate, value, model, dataset hash"
              aria-label="Search semantic graph for AI candidates"
              data-testid="ai-search-input"
              style={{
                flex: 1,
                minWidth: 0,
                border: 0,
                outline: 0,
                background: "transparent",
                color: tokens.color.ink.primary,
                fontSize: tokens.font.role.body.size,
                fontFamily: tokens.font.family.ui,
              }}
            />
          </span>
          <Stack direction="row" align="center" gap={1} style={{ flexWrap: "wrap" }}>
            <Icon name="filter_list" size={20} decorative />
            {FILTER_CHIPS.map(({ chip, label, icon, variant }) => (
              <Chip
                key={chip}
                icon={icon}
                label={label}
                variant={variant}
                pressable
                pressed={activeChips.has(chip)}
                onClick={() => toggleChip(chip)}
                data-testid={`ai-chip-filter-${chip}`}
              />
            ))}
          </Stack>
        </Stack>

        {actionError ? (
          <Banner
            severity="error"
            title="Action failed"
            body={actionError}
            data-testid="ai-action-error"
            actions={[
              { label: "Dismiss", onClick: () => setActionError(null) },
            ]}
          />
        ) : null}

        <AiCandidateList
          candidates={filtered}
          isLoading={isLoading}
          error={error ?? null}
          disabled={actionsDisabled}
          busyCandidateIds={busyCandidateIds}
          statusOverrides={statusOverrides}
          onApprove={handleApprove}
          onReject={handleReject}
          onRetry={onRetry}
          emptyMessage={
            query || activeChips.size > 0
              ? "No candidates match the current search and filter chips."
              : undefined
          }
        />

        <Stack
          direction="row"
          align="center"
          justify="space-between"
          gap={2}
          style={{
            paddingTop: tokens.space[2],
            borderTop: tokens.border.thin,
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            onClick={handlePivotReadiness}
            data-testid="ai-readiness-pivot"
            aria-label="Explain readiness gaps in the readiness card"
            style={{
              appearance: "none",
              background: "transparent",
              border: 0,
              color: tokens.color.accent.primary,
              cursor: "pointer",
              minHeight: tokens.density.touchMin,
              display: "inline-flex",
              alignItems: "center",
              gap: tokens.space[1],
              padding: tokens.space[1],
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.body.size,
            }}
          >
            <Icon name="info" size={20} decorative />
            <span>Explain readiness gaps</span>
            <Icon name="chevron_right" size={20} decorative />
          </button>

          <Button
            variant="primary"
            disabled={
              actionsDisabled ||
              publishBusy ||
              pendingForPublish.length === 0
            }
            onClick={() => setPublishOpen(true)}
            data-testid="ai-publish-cta"
            style={{
              minHeight: tokens.density.touchMin,
              display: "inline-flex",
              alignItems: "center",
              gap: tokens.space[1],
            }}
          >
            <Icon name="publish" size={20} decorative />
            <span>Publish {pendingForPublish.length} as truth</span>
          </Button>
        </Stack>
      </Stack>

      {publishOpen ? (
        <Dialog
          title={`Publish ${pendingForPublish.length} candidate${pendingForPublish.length === 1 ? "" : "s"} as truth?`}
          role="alertdialog"
          size="md"
          onClose={() => (publishBusy ? undefined : setPublishOpen(false))}
          blockBackdropClose={publishBusy}
          data-testid="ai-publish-dialog"
          actions={[
            {
              label: "Cancel",
              variant: "secondary",
              defaultFocus: true,
              onClick: () => setPublishOpen(false),
            },
            {
              label: publishBusy ? "Publishing…" : "Publish",
              variant: "primary",
              onClick: handleConfirmPublish,
            },
          ]}
        >
          <Text variant="body" as="p" style={{ margin: 0 }}>
            Publishing approved candidates promotes them from AI inference to
            source-of-truth. This change is auditable and reversible only via
            an admin override.
          </Text>
          {pendingForPublish.length === 0 ? (
            <Banner
              severity="warning"
              title="No approved candidates to publish"
              body="Approve candidates first; only approved candidates can be published as truth."
              data-testid="ai-publish-dialog-empty-warning"
            />
          ) : (
            <ul
              data-testid="ai-publish-dialog-list"
              aria-label="Candidates to publish"
              style={{
                marginTop: tokens.space[2],
                marginBottom: 0,
                paddingInlineStart: tokens.space[4],
                color: tokens.color.ink.secondary,
                fontSize: tokens.font.role.body.size,
              }}
            >
              {pendingForPublish.slice(0, 6).map((candidate) => (
                <li key={candidate.id}>
                  <code
                    style={{
                      fontFamily: tokens.font.family.mono,
                      fontSize: tokens.font.role.engMono.size,
                    }}
                  >
                    {candidate.id}
                  </code>
                  {" — "}
                  {candidate.predicate}
                </li>
              ))}
              {pendingForPublish.length > 6 ? (
                <li>+{pendingForPublish.length - 6} more</li>
              ) : null}
            </ul>
          )}
        </Dialog>
      ) : null}
    </Card>
  );
}
